/** @format */
import pRetry from 'p-retry';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import { CookieConsentCategories } from '@toucan/client/global/one-trust';

const DEFAULT_ONE_TRUST_RETRY_OPTIONS: OneTrustRetryOptions = {
    retries: 5,
    minTimeout: 500,
};

export type OneTrustRetryOptions = {
    retries: number;
    minTimeout: number;
};

/**
 * Class to manage the Bloomreach SDK integration.
 *
 * OneTrust loads asynchronously, so it is often absent when `initialise` is
 * first called. Consent is polled for rather than probed once.
 */
export class Bloomreach {
    private hasAttachedConsentHandler = false;
    private isAwaitingConsent = false;
    private customerId?: number;
    private readonly oneTrustRetryOptions: OneTrustRetryOptions;

    public constructor(
        oneTrustRetryOptions: OneTrustRetryOptions = DEFAULT_ONE_TRUST_RETRY_OPTIONS
    ) {
        this.oneTrustRetryOptions = oneTrustRetryOptions;
    }

    /**
     * Checks if the user has consented to performance cookies.
     */
    private hasConsented(): boolean {
        const performanceCategory = `,${CookieConsentCategories.PERFORMANCE},`;
        return Boolean(
            window?.OnetrustActiveGroups?.includes(performanceCategory)
        );
    }

    /**
     * Checks if the Bloomreach SDK is already initialized.
     */
    private isInitialised(): boolean {
        return Boolean(window?.exponea?.isInitialized);
    }

    /**
     * Starts the SDK against the most recently resolved customer. Guarded
     * centrally as several racing paths can reach it.
     */
    private start(): void {
        if (this.isInitialised()) {
            return;
        }

        window?.exponea?.start({ customer: { customer_id: this.customerId } });
    }

    /**
     * Resolves once OneTrust has loaded, reporting whether a consent decision
     * has already been made. Rejects if OneTrust never loads.
     */
    private waitForOneTrust(): Promise<boolean> {
        const { retries, minTimeout } = this.oneTrustRetryOptions;

        return pRetry(
            () => {
                if (!window?.OneTrust?.IsAlertBoxClosed) {
                    throw new Error('OneTrust has not loaded yet');
                }

                return Boolean(window.OneTrust.IsAlertBoxClosed());
            },
            { retries, minTimeout }
        );
    }

    /**
     * Starts the SDK if consent is already granted, otherwise attaches a
     * handler to start it when consent is granted later.
     *
     * `OnConsentChanged` only fires on an actual change, so a stored decision
     * has to be checked explicitly.
     */
    private async awaitConsent(): Promise<void> {
        if (this.isAwaitingConsent || this.hasAttachedConsentHandler) {
            return;
        }

        this.isAwaitingConsent = true;

        try {
            const isAlertBoxClosed = await this.waitForOneTrust();

            if (isAlertBoxClosed) {
                if (this.hasConsented()) {
                    this.start();
                }
                return;
            }

            this.hasAttachedConsentHandler = true;

            window.OneTrust.OnConsentChanged(() => {
                if (this.hasConsented()) {
                    this.start();
                }
            });
        } catch (error) {
            // Consent cannot be established, so do not start the SDK.
        } finally {
            this.isAwaitingConsent = false;
        }
    }

    /**
     * Initializes the Bloomreach SDK if enabled, consented, and not already initialized.
     */
    public initialise = (customerId?: number): void => {
        const {
            publicRuntimeConfig: { bloomreachEnabled },
        } = getConfig();

        if (!bloomreachEnabled || this.isInitialised()) {
            return;
        }

        // Held on the instance, not in a closure, so a late consent grant
        // identifies against the most recently resolved customer.
        this.customerId = customerId;

        if (this.hasConsented()) {
            this.start();
            return;
        }

        void this.awaitConsent();
    };
}

export const bloomreachSDK = new Bloomreach();
